import { ApolloError } from '@apollo/client';

import { isExpiredTokenError, isNetworkError } from '../../../lib/errors';

/**
 * The Apollo error message surfaced when a GraphQL request reaches the
 * backend without an `Authorization: Bearer` header — i.e. an in-flight
 * query firing while a session is being cleared during the logout /
 * token-expiry redirect race. See Sentry WEB-G.
 */
const NO_BEARER_TOKEN_MESSAGE = 'No bearer token';

/**
 * Whether a failed automation query (rules or executions) represents an
 * expected, recoverable condition rather than a real bug.
 *
 * Automations queries fire on `/automations/rules`, and during the auth
 * redirect race (e.g. landing on `/auth-callback` while a session is being
 * cleared after token expiry) they fail with a `TokenInvalid` / raw
 * `"Token is expired"` GraphQL error, a missing bearer token, or a transient
 * network failure. These are environmental conditions, not application
 * defects, so the caller downgrades them to a warning to avoid Sentry noise.
 *
 * See Sentry issues WEB-90 (executions) and WEB-8Z (rules).
 */
export const isExpectedAutomationError = (
  err: ApolloError | undefined,
): boolean => {
  if (!err) {
    return false;
  }

  if (isExpiredTokenError(err) || isNetworkError(err)) {
    return true;
  }

  return err.message?.includes(NO_BEARER_TOKEN_MESSAGE) ?? false;
};
